import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  Fab,
  Tooltip,
} from "@mui/material";
import { TaskCard } from "./Taskcard";
import { taskActionApi } from "API/apis/dashboard/taskAction";
import { useGlobalContext } from "context/GlobalContext";
import { message } from "constants/responses/message";
import { STATUS_CODES } from "constants/responses/statusCodes";
import { useTaskDashboardContext } from "../context";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { FlexBox } from "components/Wrappers/FlexBox/FlexBox";
import { routeLinks } from "constants/paths";
import { confirmAction } from "utils/functions/confirmAction";
import { RemarksModal } from "./RemarkModal";

const titles = {
  active: "🔥 Running Tasks",
  pending: "⏳ Pending Acceptance",
  acknowledge: "✅ Pending Acknowledgment",
  overdue: "🚨 Overdue Tasks",
  dueToday: "📅 Due Today Tasks",
  assigned: "📝 Assigned by Me Tasks",
};

export const TaskList = ({ tasks, filterType, searchTerm, setSearchTerm }) => {
  const navigate = useNavigate();
  const [selectedTasks, setSelectedTasks] = useState([]);
  const { setError, setSuccess, setIsLoading } = useGlobalContext();
  const { setReloadRequired, reloadRequired } = useTaskDashboardContext();
  const [openCommentModal, setOpenCommentModal] = useState(false);
  const [commentData, setCommentData] = useState();
  const [bulkAction, setBulkAction] = useState("");

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedTasks(tasks.map((task) => task.id));
    } else {
      setSelectedTasks([]);
    }
  };

  const handleSelectOne = (taskId) => {
    if (selectedTasks.includes(taskId)) {
      setSelectedTasks(selectedTasks.filter((id) => id !== taskId));
    } else {
      setSelectedTasks([...selectedTasks, taskId]);
    }
  };

  const handleOnCloseCommentModal = () => {
    setOpenCommentModal(false);
  };

  const handleBulkActionSelect = (action) => {
    setBulkAction(action);
    setOpenCommentModal(true);
  };

  const handleTaskAction = async (action) => {
    // const confirmed = confirmAction(message.info.generalConfirmBoxMessage);
    // if (!confirmed) {
    //   return;
    // }

    try {
      const { data, status } = await taskActionApi({
        commentData,
        ids: selectedTasks,
        action: bulkAction,
      });
      if (status !== STATUS_CODES.SUCCESS) {
        throw new Error(data.message);
      }
      setSuccess(message.success.updatedSuccessfully);
      setOpenCommentModal(false);
    } catch (error) {
      setError(error.message);
    }
  };

  return (
    <Box>
      <RemarksModal
        open={openCommentModal}
        onClose={handleOnCloseCommentModal}
        onSubmit={handleTaskAction}
        commentData={commentData}
        setCommentData={setCommentData}
      />
      <FlexBox justifyContent="space-between">
        <Typography variant="h5" component="h2" fontWeight="bold" mb={3}>
          {titles[filterType] || "Tasks"}
        </Typography>
        <Tooltip title="Add New Task" arrow>
          <Fab
            size="small"
            aria-label="add"
            onClick={() => navigate(routeLinks.addNewTask)}
            sx={{
              backgroundColor: "#0096FF",
              color: "#fff",
              "&:hover": {
                backgroundColor: "#0077CC", // slightly darker for hover state
              },
            }}
          >
            <AddIcon />
          </Fab>
        </Tooltip>
      </FlexBox>
      {/* --- Filter and Bulk Actions Toolbar --- */}
      <Box
        display="flex"
        flexWrap="wrap"
        alignItems="center"
        justifyContent="space-between"
        mb={3}
        gap={2}
      >
        <Box
          display="flex"
          alignItems="center"
          sx={{ flexGrow: 1, maxWidth: { xs: "100%", md: 400 } }}
        >
          <Checkbox onClick={(e) => handleSelectAll(e)} />
          <TextField
            variant="outlined"
            size="small"
            placeholder="Filter tasks by title..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flexGrow: 1, maxWidth: { xs: "100%", md: 400 } }}
          />
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="body2" color="text.secondary">
            {selectedTasks.length} selected
          </Typography>
          <FormControl size="small" disabled={selectedTasks.length === 0}>
            <Select
              value=""
              displayEmpty
              onChange={(e) => handleBulkActionSelect(e.target.value)}
            >
              <MenuItem value="">
                <em>Bulk Action...</em>
              </MenuItem>
              <MenuItem value="accept">Mark as Accepted</MenuItem>
              <MenuItem value="reject">Mark as Rejected</MenuItem>
              <MenuItem value="complete">Mark as Completed</MenuItem>
              <MenuItem value="acknowledge">Mark as Acknowledged</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>
      {/* --- Task List --- */}
      <Box display="flex" flexDirection="column" gap={2}>
        {tasks.map((task) => (
          <TaskCard
            key={task.id}
            task={task}
            filterType={filterType}
            isSelected={selectedTasks.includes(task.id)}
            onSelect={() => handleSelectOne(task.id)}
          />
        ))}
      </Box>
    </Box>
  );
};
